// CB-4B: a single data-source interface that Challenge.jsx consumes so the SAME branded markup runs
// on either the legacy user model or the cycle lifecycle. Cycle mode NEVER calls legacy endpoints
// (no toggleCompleted / saveProfile / legacy /vault / /findability-check/*), so there is no path
// from a cycle-backed Challenge to legacy state writes.
import {
  toggleCompleted, addVaultItem, updateVaultItem, deleteVaultItem, saveProfile,
  generatePost, getGeneratePostJob,
  getCycleEfforts, mutateCycleEffort, listCycleVault, createCycleVault, editCycleVault, deleteCycleVault,
} from "@/api";

const EMPTY_FOCUS = { niche: "", customNiche: "", brand: "", audience: "", audienceShort: "", searchableTitle: "", bio: "" };

// Legacy source — preserves today's exact behavior (user.completed / user.vault / user.profile).
export function makeLegacySource({ user, setUser }) {
  return {
    mode: "legacy",
    focusEditable: true,
    focus: { ...EMPTY_FOCUS, ...(user.profile || {}) },
    findabilityMode: "legacy",
    async loadInitial() {
      return { completed: user.completed || [], vault: user.vault || [] };
    },
    async saveFocus(form) { return saveProfile(form); },
    async markDone(day) {
      const r = await toggleCompleted(day, true);
      setUser((u) => ({ ...u, completed: r.completed }));
      return r.completed;
    },
    async toggleDay(day, next) {
      const r = await toggleCompleted(day, next);
      setUser((u) => ({ ...u, completed: r.completed }));
      return r.completed;
    },
    async addVault(item) {
      const r = await addVaultItem(item);
      setUser((u) => ({ ...u, vault: [r.item, ...(u.vault || [])] }));
      return { item: r.item };
    },
    async patchVault(id, patch) {
      const r = await updateVaultItem(id, patch);
      setUser((u) => ({ ...u, vault: r.vault }));
      return { vault: r.vault };
    },
    async removeVault(id) {
      const r = await deleteVaultItem(id);
      setUser((u) => ({ ...u, vault: r.vault }));
      return { vault: r.vault };
    },
    generate: (payload) => generatePost(payload),
    getGenJob: (id) => getGeneratePostJob(id),
  };
}

// Cycle source — every read/write hits /api/cycle/*; focus is the IMMUTABLE activated_focus.
// `cycleId` (optional) scopes all operations to a specific cycle when the account has multiple
// active cycles (multi-cycle flag ON). When absent, the server resolves the single active cycle.
export function makeCycleSource(cycleId) {
  let focus = { ...EMPTY_FOCUS };
  const src = {
    mode: "cycle",
    focusEditable: false,
    focus,
    findabilityMode: "cycle",
    cycleId,
    async loadInitial() {
      const [eff, v] = await Promise.all([getCycleEfforts(cycleId), listCycleVault(cycleId)]);
      focus = { ...EMPTY_FOCUS, ...(eff.focus || {}) };
      src.focus = focus;
      return { completed: eff.completed_days || [], vault: (v.items || []), focus };
    },
    async saveFocus() { /* no-op: cycle focus is immutable, never written back to profile */ },
    async markDone(day) {
      await mutateCycleEffort(day, true, cycleId);
      return (await getCycleEfforts(cycleId)).completed_days;
    },
    async toggleDay(day, next) {
      await mutateCycleEffort(day, next, cycleId);
      return (await getCycleEfforts(cycleId)).completed_days;
    },
    async addVault(item) {
      const r = await createCycleVault(item, cycleId);
      return { item: r.item };
    },
    async patchVault(id, patch) {
      await editCycleVault(id, patch);
      return { vault: (await listCycleVault(cycleId)).items };
    },
    async removeVault(id) {
      await deleteCycleVault(id);
      return { vault: (await listCycleVault(cycleId)).items };
    },
    generate: (payload) => generatePost(cycleId ? { ...payload, cycle_id: cycleId } : payload),
    getGenJob: (id) => getGeneratePostJob(id),
  };
  return src;
}
